import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function ResourcesSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Resources");
  const [heroHeading, setHeroHeading] = useState(
    "Free Resources to Guide Your",
  );
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState(
    "Digital Transformation",
  );
  const [heroDescription, setHeroDescription] = useState("");
  const [heroCTAText, setHeroCTAText] = useState("Browse All Resources");
  const [heroCTALink, setHeroCTALink] = useState("#resource-categories");

  /* ---------------- RESOURCE CATEGORIES ---------------- */
  const [catsActive, setCatsActive] = useState(true);
  const [catsBadgeText, setCatsBadgeText] = useState("Explore");
  const [catsHeading, setCatsHeading] = useState(
    "What Would You Like to Explore?",
  );
  const [catsCards, setCatsCards] = useState([
    { title: "", description: "", icon: "", href: "" },
  ]);

  /* ---------------- FEATURED RESOURCES ---------------- */
  const [featActive, setFeatActive] = useState(true);
  const [featBadgeText, setFeatBadgeText] = useState("Most Popular");
  const [featHeading, setFeatHeading] = useState("Most Popular Resources");
  const [featCards, setFeatCards] = useState([
    { title: "", description: "", href: "", icon: "", cta: "" },
  ]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState(
    "Ready to Transform Your Business?",
  );
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaText, setCtaText] = useState("Book a Free Consultation");
  const [ctaContactLine, setCtaContactLine] = useState(
    "+61 3 7075 3900 | info@thynkprocess.com.au",
  );

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      setHeroCTAText(d.hero.cta_text || "");
      setHeroCTALink(d.hero.cta_link || "");
    }

    if (d.resource_categories) {
      setCatsBadgeText(d.resource_categories.badge_text || "");
      setCatsHeading(d.resource_categories.heading || "");
      if (d.resource_categories.cards?.length)
        setCatsCards(d.resource_categories.cards);
    }

    if (d.featured_resources) {
      setFeatBadgeText(d.featured_resources.badge_text || "");
      setFeatHeading(d.featured_resources.heading || "");
      if (d.featured_resources.cards?.length)
        setFeatCards(d.featured_resources.cards);
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaDescription(d.final_cta.description || "");
      setCtaText(d.final_cta.cta_text || "");
      setCtaContactLine(d.final_cta.contact_line || "");
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
      cta_text: heroCTAText,
      cta_link: heroCTALink,
    },
    resource_categories: {
      badge_text: catsBadgeText,
      heading: catsHeading,
      cards: catsCards,
    },
    featured_resources: {
      badge_text: featBadgeText,
      heading: featHeading,
      cards: featCards,
    },
    final_cta: {
      heading: ctaHeading,
      description: ctaDescription,
      cta_text: ctaText,
      contact_line: ctaContactLine,
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    heroCTAText,
    heroCTALink,
    catsBadgeText,
    catsHeading,
    catsCards,
    featBadgeText,
    featHeading,
    featCards,
    ctaHeading,
    ctaDescription,
    ctaText,
    ctaContactLine,
  ]);

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={heroCTAText}
              onChange={(e) => setHeroCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={heroCTALink}
              onChange={(e) => setHeroCTALink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* RESOURCE CATEGORIES */}
      <CollapsibleSection
        title="Resource Categories"
        active={catsActive}
        onToggleActive={setCatsActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={catsBadgeText}
            onChange={(e) => setCatsBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={catsHeading}
            onChange={(e) => setCatsHeading(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Category Cards</p>
          <button
            onClick={() =>
              setCatsCards((s) => [
                ...s,
                { title: "", description: "", icon: "", href: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Category
          </button>
        </div>
        {catsCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Category {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={catsCards.length}
                  onMove={(f, t) => setCatsCards(moveItem(catsCards, f, t))}
                />
                {catsCards.length > 1 && (
                  <button
                    onClick={() =>
                      setCatsCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setCatsCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setCatsCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Link (href)">
              <TextInput
                value={c.href}
                onChange={(e) =>
                  setCatsCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, href: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setCatsCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* FEATURED RESOURCES */}
      <CollapsibleSection
        title="Featured Resources"
        active={featActive}
        onToggleActive={setFeatActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={featBadgeText}
            onChange={(e) => setFeatBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={featHeading}
            onChange={(e) => setFeatHeading(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Resource Cards</p>
          <button
            onClick={() =>
              setFeatCards((s) => [
                ...s,
                { title: "", description: "", href: "", icon: "", cta: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Resource
          </button>
        </div>
        {featCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Resource {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={featCards.length}
                  onMove={(f, t) => setFeatCards(moveItem(featCards, f, t))}
                />
                {featCards.length > 1 && (
                  <button
                    onClick={() =>
                      setFeatCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setFeatCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setFeatCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Link (href)">
              <TextInput
                value={c.href}
                onChange={(e) =>
                  setFeatCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, href: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="CTA Text">
              <TextInput
                value={c.cta}
                placeholder="e.g. Use the Calculator"
                onChange={(e) =>
                  setFeatCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, cta: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setFeatCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <Field label="CTA Button Text">
          <TextInput
            value={ctaText}
            onChange={(e) => setCtaText(e.target.value)}
          />
        </Field>
        <Field label="Contact Line">
          <TextInput
            value={ctaContactLine}
            placeholder="+61 3 7075 3900 | info@thynkprocess.com.au"
            onChange={(e) => setCtaContactLine(e.target.value)}
          />
        </Field>
      </CollapsibleSection>
    </>
  );
}
